import clsx from 'clsx';
import { Edit, MessageSquare, MoreHorizontal, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, IconButton } from '@doger/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@doger/ui/components/ui/dropdown-menu';
import { ChatConfig } from '@doger/core/modules/chat';
import type { SessionResponse } from '@doger/core/shared/api';

import { useTranslation } from '../../../shared/i18n';
import { useSessionIdFromParams } from '../router';
import { RenamingChat } from './rename';
import { useDeleteChatMutation } from './use-delete-chat.mutation';
import styles from './chat-list-item.module.scss';

type ChatListItemProps = {
  session: SessionResponse;
  onChatClick: (chatId: string) => void;
};

export const ChatListItem = ({ session, onChatClick }: ChatListItemProps) => {
  const sessionId = useSessionIdFromParams();
  const navigate = useNavigate();
  const { t } = useTranslation('app');

  const [isRenaming, setIsRenaming] = useState(false);
  const [isAppearing, setIsAppearing] = useState(true);
  const [isDisappearing, setIsDisappearing] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isActive = session.id === sessionId;

  const title =
    session.meta.title === ChatConfig.DEFAULT_TITLE
      ? (session.state?.title ?? session.meta.title)
      : session.meta.title;

  const deleteChat = useDeleteChatMutation({
    chatId: session.id,
    isActive,
  });

  // Trigger appearing animation when component mounts
  useEffect(() => {
    setIsAppearing(true);
    const timer = setTimeout(() => setIsAppearing(false), 300);

    return () => clearTimeout(timer);
  }, []);

  const handleClick = () => {
    if (!isRenaming) {
      navigate(`/c/${session.id}`);
      onChatClick?.(session.id!);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDisappearing(true);

    setTimeout(() => {
      deleteChat.mutateAsync(session.id!);
    }, 300);
  };

  const handleRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsRenaming(true);
  };

  return (
    <Button
      className={clsx(styles.chatListItemContainer, {
        [Button.classnames.contained.hover]: isActive || isMenuOpen,
        [styles.chatListItemActive]: isActive,
        [styles.chatListItemAppearing]: isAppearing,
        [styles.chatListItemDisappearing]: isDisappearing,
      })}
      variant='contained'
      size='lg'
      palette='ghost'
      onClick={handleClick}
    >
      <MessageSquare
        className={clsx(styles.chatListItemIcon, {
          [styles.chatListItemIconActive]: isActive,
        })}
      />

      <div className={styles.chatListItemContent}>
        {isRenaming ? (
          <RenamingChat
            chatId={session.id}
            currentTitle={title}
            onSuccess={() => {
              setIsRenaming(false);
            }}
            onCancel={() => {
              setIsRenaming(false);
            }}
          />
        ) : (
          <div className={styles.chatListItemTitle} title={title}>
            {title}
          </div>
        )}
      </div>

      <DropdownMenu onOpenChange={setIsMenuOpen} modal={false}>
        <DropdownMenuTrigger asChild>
          <IconButton
            className={styles.dropdownMenuTrigger}
            size='sm'
            variant='text'
            onClick={(e) => e.stopPropagation()}
            tabIndex={isActive ? 0 : -1}
          >
            <MoreHorizontal className='h-3 w-3' />
          </IconButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent className={styles.dropdownMenu} align='start'>
          <DropdownMenuItem onClick={handleRename}>
            <Edit />

            <span>{t('chatActions.rename')}</span>
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleDelete} palette='danger'>
            <Trash2 />

            <span>{t('chatActions.delete')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </Button>
  );
};
